import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Snackbar, Alert, AlertTitle, Button, Stack, Slide } from '@mui/material';
import type { SlideProps } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ACCOUNTING_API } from '../../config/api';
import { accountingApi } from '../../config/apiClient';
import { useAuth } from '../../contexts/AuthContext';

/**
 * LoanDueNotifier — Uygulama geneli kredi vade uyarıları.
 *
 * Girişte (mount) ve her 60 dakikada bir vadesi geçmiş + vadesine 5 gün kalan
 * kredi taksitlerini kontrol eder; varsa sağ alttan kalıcı bildirim gösterir.
 * Günde bir kez gösterilir (localStorage damgası, şirket bazlı); taksit
 * ödenmediği sürece ertesi gün damga eskidiği için bildirim yeniden gelir.
 */

interface UpcomingInstallment {
  loanId: number;
  loanName: string;
  bankAccountName?: string | null;
  installmentId: number;
  installmentNo: number;
  dueDate: string;
  amount: number;
  currency: string;
  daysToDue: number;
}

const CHECK_INTERVAL_MS = 60 * 60 * 1000; // 60 dakika

const todayStamp = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Para birimine göre toplayıp "12.500,00 ₺ + 300,00 $" biçiminde döndürür */
const sumByCurrency = (items: UpcomingInstallment[], locale: string): string => {
  const totals: Record<string, number> = {};
  items.forEach((i) => {
    const cur = i.currency || 'TRY';
    totals[cur] = (totals[cur] || 0) + Number(i.amount || 0);
  });
  const sym = (c: string) => (c === 'TRY' ? '₺' : c === 'USD' ? '$' : c === 'EUR' ? '€' : c);
  return Object.entries(totals)
    .map(([cur, total]) => `${total.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${sym(cur)}`)
    .join(' + ');
};

const SlideLeft = (props: SlideProps) => <Slide {...props} direction="left" />;

const LoanDueNotifier: React.FC = () => {
  const navigate = useNavigate();
  const { userDetails } = useAuth();
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const tt = (tr: string, en: string) => (isTr ? tr : en);
  const localeCode = isTr ? 'tr-TR' : 'en-US';

  const [open, setOpen] = useState(false);
  const [overdue, setOverdue] = useState<UpcomingInstallment[]>([]);
  const [upcoming, setUpcoming] = useState<UpcomingInstallment[]>([]);
  const checkingRef = useRef(false);

  const companyId = userDetails?.companyId;
  const stampKey = companyId ? `loanDueNotifiedAt:${companyId}` : null;

  const writeStamp = useCallback(() => {
    if (stampKey) {
      try {
        localStorage.setItem(stampKey, todayStamp());
      } catch { /* localStorage erişilemezse sessizce geç */ }
    }
  }, [stampKey]);

  const check = useCallback(async () => {
    if (!companyId || !stampKey || checkingRef.current) return;

    // Günde bir kez gösterim kuralı
    try {
      if (localStorage.getItem(stampKey) === todayStamp()) return;
    } catch { /* okunamazsa kontrole devam et */ }

    checkingRef.current = true;
    try {
      const res = await accountingApi.get(ACCOUNTING_API.BANK_LOAN_UPCOMING(5));
      const items: UpcomingInstallment[] = res.data?.data || [];
      if (items.length === 0) return;

      const overdueItems = items.filter((i) => i.daysToDue < 0);
      const upcomingItems = items.filter((i) => i.daysToDue >= 0 && i.daysToDue <= 5);
      if (overdueItems.length === 0 && upcomingItems.length === 0) return;

      setOverdue(overdueItems);
      setUpcoming(upcomingItems);
      setOpen(true);
    } catch (err) {
      // Bildirim sistemi uygulamayı asla bloklamaz
      console.warn('[LoanDueNotifier] Vade kontrolü yapılamadı:', err);
    } finally {
      checkingRef.current = false;
    }
  }, [companyId, stampKey]);

  useEffect(() => {
    if (!companyId) return;
    check();
    const interval = setInterval(check, CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [companyId, check]);

  const handleClose = () => {
    setOpen(false);
    writeStamp();
  };

  const handleGoToLoans = () => {
    setOpen(false);
    writeStamp();
    navigate('/krediler');
  };

  if (!companyId || (!overdue.length && !upcoming.length)) return null;

  // En gecikmiş taksit (daysToDue en küçük) ve en yakın vade (daysToDue en küçük pozitif)
  const mostOverdue = overdue.length
    ? overdue.reduce((a, b) => (a.daysToDue < b.daysToDue ? a : b))
    : null;
  const nearest = upcoming.length
    ? upcoming.reduce((a, b) => (a.daysToDue < b.daysToDue ? a : b))
    : null;

  const goButton = (
    <Button color="inherit" size="small" onClick={handleGoToLoans} sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
      {tt('Kredilere Git', 'Go to Loans')}
    </Button>
  );

  return (
    <Snackbar
      open={open}
      onClose={(_e, reason) => { if (reason !== 'clickaway') handleClose(); }}
      autoHideDuration={null}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      TransitionComponent={SlideLeft}
      sx={{ maxWidth: 480 }}
    >
      <Stack spacing={1}>
        {overdue.length > 0 && (
          <Alert severity="error" variant="filled" onClose={handleClose} action={goButton} sx={{ width: '100%' }}>
            <AlertTitle>
              {tt(
                `⚠️ Vadesi GEÇMİŞ ${overdue.length} kredi taksidi var — toplam ${sumByCurrency(overdue, localeCode)}`,
                `⚠️ ${overdue.length} loan installment(s) OVERDUE — total ${sumByCurrency(overdue, localeCode)}`
              )}
            </AlertTitle>
            {mostOverdue && tt(
              `${mostOverdue.loanName} — ${Math.abs(mostOverdue.daysToDue)} gün gecikti`,
              `${mostOverdue.loanName} — ${Math.abs(mostOverdue.daysToDue)} day(s) late`
            )}
          </Alert>
        )}
        {upcoming.length > 0 && (
          <Alert severity="warning" variant="filled" onClose={handleClose} action={goButton} sx={{ width: '100%' }}>
            <AlertTitle>
              {tt(
                `⏰ Vadesi yaklaşan ${upcoming.length} kredi taksidi var — toplam ${sumByCurrency(upcoming, localeCode)}`,
                `⏰ ${upcoming.length} loan installment(s) due soon — total ${sumByCurrency(upcoming, localeCode)}`
              )}
            </AlertTitle>
            {nearest && tt(
              `En yakın vade: ${nearest.loanName}, ${new Date(nearest.dueDate).toLocaleDateString(localeCode)}`,
              `Nearest due: ${nearest.loanName}, ${new Date(nearest.dueDate).toLocaleDateString(localeCode)}`
            )}
          </Alert>
        )}
      </Stack>
    </Snackbar>
  );
};

export default LoanDueNotifier;
